import React, { useEffect, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import OrderAutocomplete from '@/modules/commons/components/OrderAutocomplete';
import type { OrderAutocompleteItem } from '@/modules/commons/components/OrderAutocomplete';
import axiosInstance from '@/setup/axios/axiosInstance';

import { AllocationLine, InventoryAllocationFormData } from '../types';

interface AllocationFormProps {
  formData: InventoryAllocationFormData;
  canEdit?: boolean;
  saving?: boolean;
  isDirty?: boolean;
  onChangeForm: (updates: Partial<InventoryAllocationFormData>) => void;
  onInitializeForm?: (updates: Partial<InventoryAllocationFormData>) => void;
  onChangeLine: (lineId: number, updates: Partial<AllocationLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: number) => void;
  onSave: () => void;
  onCancel?: () => void;
  orderOptions: OrderAutocompleteItem[];
  loadingOrders: boolean;
  orderSearchInput: string;
  onOrderSearchChange: (value: string) => void;
  storeOptions: Array<{ id: string; name: string }>;
  loadingStores: boolean;
  storeSearchInput: string;
  onStoreSearchChange: (value: string) => void;
  unitOptions: Array<{ id: string; name: string }>;
  loadingUnits: boolean;
  itemOptions: Array<{ id: string; name: string; code?: string }>;
  loadingItems: boolean;
}

export default function AllocationForm({
  formData,
  canEdit = true,
  saving = false,
  isDirty = false,
  onChangeForm,
  onInitializeForm = onChangeForm,
  onChangeLine,
  onAddLine,
  onRemoveLine,
  onSave,
  onCancel = () => {},
  orderOptions,
  loadingOrders,
  orderSearchInput,
  onOrderSearchChange,
  storeOptions,
  loadingStores,
  storeSearchInput,
  onStoreSearchChange,
  unitOptions,
  loadingUnits,
  itemOptions,
  loadingItems,
}: AllocationFormProps) {
  const readOnly = !canEdit;

  const [statusOptions, setStatusOptions] = useState<Array<{ id: string; name: string; code?: string; color?: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get('/settings/statuses/by-module/', {
          params: { module: 'allocation' },
        });
        if (cancelled) return;
        const data = response.data;
        const allocationData = data?.allocation || data;
        const groups = allocationData?.groups || [];
        const items = groups.flatMap((group: any) => group?.items || []);
        if (items.length > 0) {
          setStatusOptions(
            items.map((item: any) => ({
              id: String(item.id),
              name: item.name || '',
              code: item.code || undefined,
              color: item.color || undefined,
            }))
          );
          const defaultStatus = items.find((item: any) => item.is_default);
          if (defaultStatus) {
            const currentStatus = normalizeStatusValue(formData.status);
            const isCurrentValid = items.some(
              (item: any) =>
                normalizeStatusValue(item.id) === currentStatus ||
                normalizeStatusValue(item.code) === currentStatus
            );
            if (!isCurrentValid) {
              onInitializeForm({
                status: String(defaultStatus.id),
                status_color: defaultStatus.color || '',
              });
            }
          }
        }
      } catch {
        // statusOptions stays empty
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const normalizeStatusValue = (value?: string | null) => (value || '').trim().toUpperCase();

  const selectedStatus = statusOptions.find((status) => {
    const currentStatus = normalizeStatusValue(formData.status);
    return normalizeStatusValue(status.id) === currentStatus || normalizeStatusValue(status.code) === currentStatus;
  }) || null;

  const statusColor = selectedStatus?.color || '';

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={onCancel}
      submitting={saving}
      submitDisabled={saving || readOnly || !isDirty}
      submitLabel='Lưu'
      submittingLabel='Đang lưu...'
      containerSx={{ m: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Thông tin allocation</Typography>

        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Đơn hàng' labelWidth={100}>
              <OrderAutocomplete
                orders={orderOptions}
                loading={loadingOrders}
                inputValue={orderSearchInput}
                onInputChange={onOrderSearchChange}
                value={formData.order_no || null}
                onChange={(orderId) => onChangeForm({ order_no: orderId || '' })}
                disabled={readOnly}
                placeholder='Nhập để tìm đơn hàng'
              />
            </LabeledField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho' labelWidth={100}>
              <Autocomplete
                fullWidth
                options={storeOptions}
                loading={loadingStores}
                inputValue={storeSearchInput}
                onInputChange={(_, value) => onStoreSearchChange(value)}
                filterOptions={(options) => options}
                value={storeOptions.find((s) => s.id === formData.store_name) || null}
                onChange={(_, newValue) => onChangeForm({ store_name: newValue?.id || '' })}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth placeholder='Nhập để tìm kho' />
                )}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as any;
                  return (
                    <Box component='li' key={key} {...rest}>
                      <Typography variant='body2'>{option.name}</Typography>
                    </Box>
                  );
                }}
                noOptionsText={storeSearchInput ? 'Không tìm thấy kho' : 'Nhập để tìm kho'}
              />
            </LabeledField>
            <LabeledField label='Trạng thái' labelWidth={100}>
              <Autocomplete
                options={statusOptions}
                value={selectedStatus}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ padding: 0, margin: 0 }}>
                    <Typography sx={{ color: option.color || 'inherit', px: 2, py: 0.5 }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder='Chọn trạng thái'
                    size='small'
                    variant='standard'
                    InputProps={{
                      ...params.InputProps,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: statusColor || formData.status_color || 'inherit',
                        fontWeight: statusColor || formData.status_color ? 500 : 'inherit',
                      },
                    }}
                  />
                )}
                onChange={(_event, newValue) => {
                  const nextStatus = newValue as { id: string; name: string; color?: string } | null;
                  onChangeForm({
                    status: nextStatus?.id || '',
                    status_color: nextStatus?.color || '',
                  });
                }}
                noOptionsText='Không tìm thấy trạng thái'
                disabled={readOnly}
                fullWidth
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Danh sách vật tư</Typography>

        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Vật tư</TableCell>
                <TableCell width={180}>ĐVT</TableCell>
                <TableCell width={120} align='center'>Số lượng</TableCell>
                <TableCell width={60} align='center'>&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!formData.lines || formData.lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 4, color: '#94a3b8' }}>
                    Chưa có vật tư nào. Nhấn "Thêm vật tư" để thêm.
                  </TableCell>
                </TableRow>
              ) : (
                (formData.lines || []).map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <Autocomplete
                        size='small'
                        fullWidth
                        options={itemOptions}
                        loading={loadingItems}
                        filterOptions={(options) => options}
                        value={itemOptions.find((i) => i.id === line.item_id) || null}
                        onChange={(_, newValue) => onChangeLine(line.id, {
                          item_id: newValue?.id || '',
                          item_name: newValue?.name || '',
                          item_code: newValue?.code || '',
                        })}
                        getOptionLabel={(option) => option.name || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={readOnly}
                        renderInput={(params) => (
                          <TextField {...params} size='small' placeholder='Nhập để tìm vật tư' />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...rest } = props as any;
                          return (
                            <Box component='li' key={key} {...rest}>
                              <Typography variant='body2'>
                                {option.code ? `${option.code} - ` : ''}{option.name}
                              </Typography>
                            </Box>
                          );
                        }}
                        noOptionsText='Không tìm thấy vật tư'
                      />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size='small'
                        fullWidth
                        options={unitOptions}
                        loading={loadingUnits}
                        filterOptions={(options) => options}
                        value={unitOptions.find((u) => u.id === line.unit_id) || null}
                        onChange={(_, newValue) => onChangeLine(line.id, {
                          unit_id: newValue?.id || '',
                          unit_name: newValue?.name || '',
                        })}
                        getOptionLabel={(option) => option.name || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={readOnly}
                        renderInput={(params) => (
                          <TextField {...params} size='small' placeholder='Chọn ĐVT' />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...rest } = props as any;
                          return (
                            <Box component='li' key={key} {...rest}>
                              <Typography variant='body2'>{option.name}</Typography>
                            </Box>
                          );
                        }}
                        noOptionsText='Không tìm thấy ĐVT'
                      />
                    </TableCell>
                    <TableCell align='center'>
                      <TextField
                        size='small'
                        type='number'
                        value={line.qty}
                        onChange={(e) => onChangeLine(line.id, { qty: e.target.value })}
                        disabled={readOnly}
                        inputProps={{ min: 0, step: 0.001, style: { textAlign: 'center', width: 90 } }}
                      />
                    </TableCell>
                    <TableCell align='center'>
                      <Tooltip title='Xóa'>
                        <IconButton color='error' size='small' onClick={() => onRemoveLine(line.id)} disabled={readOnly}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 1.5 }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={<AddIcon />}
            disabled={readOnly}
            onClick={onAddLine}
            sx={{ textTransform: 'none' }}
          >
            Thêm vật tư
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Ghi chú</Typography>
        <TextareaAutosize
          minRows={4}
          value={formData.note || ''}
          onChange={(event) => onChangeForm({ note: event.target.value })}
          disabled={readOnly}
          placeholder='Nhập ghi chú chung cho allocation...'
          style={sharedFormStyles.additionalTextarea(canEdit)}
        />
      </Paper>
    </AppForm>
  );
}
